<template>
  <div class="sidebar-item hasChildren" :class="{ open: state.isOpen, current: currentRoute }">
    <div class="primary-wrapper" @click="toggleFetch">
      <img :src="space.image_id ? getImage(space.image_id) : getRandomSpacePicture(space.name)" alt="Logo" class="spaceLogo" />
      <transition-group name="fade">
        <span v-if="animationIsFinished && sidebarIsOpen" v-text="space.name" />
        <i v-if="animationIsFinished && sidebarIsOpen" class="bi bi-chevron-down toggle-children" />
      </transition-group>
    </div>
    <div class="children-wrapper" :style="{ height: `${childrenWrapperHeight}px` }">
      <template v-if="!isPending && spaceCampaigns.length">
        <router-link
          v-for="(item, index) in spaceCampaigns"
          :key="index"
          :to="{
            name: 'campaign',
            params: {
              spaceId: item.campaign.parent_workspace_id,
              campaignId: item.id,
            },
          }"
          class="children-item"
        >
          <span v-text="item.campaign.internal_name" />
        </router-link>
      </template>
      <template v-else>
        <p class="children-item disabled">{{ $t("app.sidebar.noCampaign") }}</p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { getImage, getRandomSpacePicture } from "@/composables/getImage.js";
import { campaignsApi } from "@/api/campaigns.api";
import { useQuery } from "@tanstack/vue-query";

const route = useRoute();

const props = defineProps({
  space: {
    type: Object,
    required: true,
  },
  animationIsFinished: {
    type: Boolean,
    required: true,
  },
  sidebarIsOpen: {
    type: Boolean,
    required: true,
  },
});

const state = reactive({
  isOpen: false,
});

const enabled = computed(() => {
  return state.isOpen;
})

const { data: spaceCampaigns, isPending } = useQuery({
  queryKey: ['campaigns', { organisationId: route.params.companyId, spaceId: props.space.workspace_id }],
  queryFn: () => campaignsApi.getAll(route.params.companyId as string, props.space.workspace_id as string),
  enabled: enabled,
});

const currentRoute = computed(() => {
  if (isPending.value || !spaceCampaigns.value) return false;

  return spaceCampaigns.value.find((child) => child.campaign.parent_workspace_id === route.params.spaceId);
});

const childrenWrapperHeight = computed(() => {
  if (!state.isOpen || !spaceCampaigns.value) return 0;

  const childHasDivider = spaceCampaigns.value.filter((child) => child.divider);

  let nbchild = childHasDivider.length > 0 ? spaceCampaigns.value.length + 1 : spaceCampaigns.value.length;

  nbchild = nbchild === 0 ? 1 : nbchild;

  // Nb of child with 20px height + gap of 16px between
  return nbchild * 20 + (nbchild - 1) * 16;
});

watch(props, () => {
  if (!props.sidebarIsOpen) {
    state.isOpen = false;
  }
});

async function toggleFetch() {
  state.isOpen = !state.isOpen;
}
</script>
